import { ZendraLocalizationPipe } from '../pipes/zendra-localization.pipe';

import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  computed,
  signal,
  inject,
} from '@angular/core';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { SummaryService } from './summary-service';
import { ColumnType, Operation } from './models/summary.types';
import { OperationOption } from './interfaces/IOperationOption';

@Component({
  selector: 'zn-summary',
  imports: [ZendraLocalizationPipe],
  templateUrl: './summary.html',
  styleUrl: './summary.scss',
})
export class Summary implements OnInit, OnChanges {
  @Input() columnId!: string;
  @Input() columnField!: string;
  @Input() columnType!: ColumnType;
  @Input() selectedRows: any[] = [];

  readonly service = inject(SummaryService);

  selectedOperation = signal<Operation>('none');
  selectedOption = signal<OperationOption>({});
  private baseOperations = signal<OperationOption[]>([]);
  private calculatedValue = signal<string | number | null>(null);
  private showResult = signal<boolean>(false);

  localizationResource = ZendraLocalizationServices;

  isDropdownEnabled = computed(() => this.availableOperations().length > 0);
  availableOperations = computed(() => this.baseOperations());

  hasResult = computed(() => this.showResult() && this.calculatedValue() !== null);

  currentDisplayLabel = computed(() => {
    if (this.hasResult()) {
      return `${this.calculatedValue()}`;
    }

    if (!this.isDropdownEnabled()) {
      return 'N/A';
    }
    return null;
  });

  displayOptions = computed(() => {
    const operations = this.availableOperations();
    const hasResult = this.hasResult();

    if (hasResult) {
      return [
        ...operations,
        { value: 'none' as const, label: 'InputResumeReset', icon: 'bi bi-eraser' },
      ];
    } else {
      return operations;
    }
  });

  ngOnInit() {
    this.setAvailableOperations();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['columnType']) {
      this.setAvailableOperations();
    }

    if (changes['selectedRows'] && !changes['selectedRows'].firstChange) {
      this.calculate();
    }
  }

  private setAvailableOperations() {
    let operations: OperationOption[] = [];

    switch (this.columnType) {
      case 'number':
        operations = [
          { value: 'sum', label: 'InputResumeAdd', icon: 'zn-sigma' },
          { value: 'min', label: 'InputResumeMin', icon: 'bi bi-arrow-down' },
          { value: 'max', label: 'InputResumeMax', icon: 'bi bi-arrow-up' },
          {
            value: 'average',
            label: 'InputResumeAVG',
            icon: 'bi bi-distribute-vertical',
          },
        ];
        break;

      case 'text':
        operations = [
          { value: 'min', label: 'InputResumeMin', icon: 'bi bi-arrow-down' },
          { value: 'max', label: 'InputResumeMax', icon: 'bi bi-arrow-up' },
        ];
        break;

      case 'date':
        operations = [
          { value: 'min', label: 'InputResumeMin', icon: 'bi bi-arrow-down' },
          { value: 'max', label: 'InputResumeMax', icon: 'bi bi-arrow-up' },
        ];
        break;

      case 'boolean':
        operations = [];
        break;
    }
    this.baseOperations.set(operations);
  }

  onOperationChange(option: OperationOption) {
    if (option.value === 'none') {
      this.selectedOperation.set('none');
      this.selectedOption.set({});
      this.calculatedValue.set(null);
      this.showResult.set(false);

      this.service.setTracker({
        columnId: this.columnId,
        operation: 'none',
      });
      return;
    }

    this.showResult.set(false);

    this.selectedOperation.set(option.value);
    this.selectedOption.set(option);

    this.service.setTracker({
      columnId: this.columnId,
      operation: option.value,
    });
    this.calculate();

    setTimeout(() => {
      this.showResult.set(true);
    }, 0);
  }

  updateSelectedRows(rows: any[]) {
    this.selectedRows = rows;

    if (this.selectedOperation() !== 'none') {
      this.calculate();
    }
  }

  calculateTrakedOperation(operation: OperationOption) {
    this.onOperationChange(operation);
  }

  findOperationOption(operation: Operation): OperationOption | undefined {
    return this.availableOperations().find(op => op.value === operation);
  }

  private calculate() {
    const operation = this.selectedOperation();

    if (operation === 'none' || this.selectedRows.length === 0) {
      this.calculatedValue.set(null);
      return;
    }

    const values = this.selectedRows
      .map(row => {
        if (!this.columnField || !row) return undefined;
        if (this.columnField.includes('.')) {
          return this.columnField.split('.').reduce((current, part) => current?.[part], row);
        }
        return row[this.columnField];
      })
      .filter(val => val !== null && val !== undefined);

    let result: string | number = 'Error';

    switch (this.columnType) {
      case 'number':
        result = this.calculateNumber(values, operation);
        break;

      case 'text':
        result = this.calculateText(values, operation);
        break;

      case 'date':
        result = this.calculateDate(values, operation);
        break;
    }
    this.calculatedValue.set(result);
  }

  private calculateNumber(values: number[], operation: Operation): string {
    const numValues = values.map(v => Number(v));

    switch (operation) {
      case 'sum':
        const sum = numValues.reduce((accumulator, value) => accumulator + value, 0);
        return sum.toFixed(2);

      case 'average':
        const avg =
          numValues.reduce((accumulator, value) => accumulator + value, 0) /
          numValues.length;
        return avg.toFixed(2);

      case 'min':
        return Math.min(...numValues).toFixed(2);

      case 'max':
        return Math.max(...numValues).toFixed(2);

      default:
        return '0';
    }
  }

  private calculateText(values: string[], operation: Operation): string {
    const sorted = [...values].sort((a, b) => {
      return a.toLowerCase().localeCompare(b.toLowerCase());
    });

    switch (operation) {
      case 'max':
        return sorted[sorted.length - 1];

      case 'min':
        return sorted[0];

      default:
        return '';
    }
  }

  private calculateDate(values: string[], operation: Operation): string {
    const dates = values.map(v => new Date(v));

    let resultDate: Date;

    switch (operation) {
      case 'max':
        resultDate = new Date(Math.max(...dates.map(d => d.getTime())));
        break;
      case 'min':
        resultDate = new Date(Math.min(...dates.map(d => d.getTime())));
        break;
      default:
        return '';
    }

    return this.formatDateUTC(resultDate); 
  }

  private formatDateUTC(date: Date): string {
    const day   = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year  = String(date.getUTCFullYear());
    return `${day}/${month}/${year}`;
  }
}
